/**
 * The venue card, and the lists made of it.
 *
 * One card serves every screen — list, nearby, favourites, a programme's
 * venues, search results — so a venue reads the same wherever it turns up. One
 * thing varies by context: whether the distance is shown next to the zone.
 *
 * The whole box opens the venue. The button covering it is still the one
 * around the name, stretched over the card in CSS, so there is one named
 * control per card rather than a `div` listening for taps.
 *
 * Everything goes in as text through `el`, never as markup: the catalogue is
 * hand-edited, and a name is a name, not HTML.
 */

import {
  STATUS_LABELS,
  el,
  fold,
  formatEpisode,
  highlight,
  outcomeLabel,
} from './format.js';
import { distanceKm, formatDistance } from './geo.js';
import { icon } from './icons.js';
import { venuePhoto } from './photo.js';
import { isGuide, starsGlyph } from './guida.js';
import { programPill } from './programs.js';
import { venueEpisodeKey } from './puntate.js';

function favouriteButton(venue, { state, onToggleFav }) {
  const saved = state.fav.has(venue.id);
  const button = el('button', saved ? 'fav is-on' : 'fav');
  button.type = 'button';
  button.setAttribute('aria-pressed', String(saved));
  button.setAttribute(
    'aria-label',
    saved ? `Togli ${venue.nome} dai preferiti` : `Salva ${venue.nome} nei preferiti`
  );
  button.append(icon('heart'));
  button.addEventListener('click', () => onToggleFav(venue.id));
  return button;
}

/**
 * Why a result is a result, when nothing visible on the card says so.
 *
 * The name and the zone carry the marks, and most of the time that is the
 * whole story. But a venue also matches on its street and on the name it had
 * on television, and neither is printed on the card: searching a street
 * produced a list of places with nothing highlighted in them, which reads as
 * a broken search rather than as a good answer.
 *
 * So the field that did match gets one quiet line of its own, and only in the
 * case where it is needed. The programme is deliberately not among them: a
 * query matching a programme name is already explained by the pill sitting
 * right there with that name on it.
 */
function matchNote(venue, query) {
  const wanted = fold(query ?? '').trim();
  if (!wanted) return null;

  const shown = fold(`${venue.nome ?? venue.id} ${venue.zona ?? ''}`);
  if (shown.includes(wanted)) return null;

  const elsewhere = [
    ['In onda come ', venue.nome_in_onda],
    ['', venue.indirizzo],
  ];

  for (const [prefix, value] of elsewhere) {
    if (!value || !fold(value).includes(wanted)) continue;
    const line = el('p', 'card__match');
    if (prefix) line.append(document.createTextNode(prefix));
    line.append(highlight(value, query));
    return line;
  }

  return null;
}

export function createCard(
  venue,
  ctx,
  { distance = false, address = false, episodeLink = true } = {}
) {
  const { programNames, state, onOpen } = ctx;

  const card = el('div', state.attivo === venue.id ? 'card is-active' : 'card');
  card.dataset.venueId = venue.id;

  const row = el('div', 'card__row');
  // Same rule as the venue card: a thumbnail only when there is a photograph.
  // A column of identical empty boxes took a fifth of the row and said nothing.
  if (venue.foto) row.append(venuePhoto(venue, 'card'));

  const open = el('button', 'card__open');
  open.type = 'button';
  // What the button does, said after what it shows: a hidden tail on its own
  // text rather than an `aria-label`, which replaced the name and zone a
  // sighted user sees with different words — voice control users say what
  // they see, and the two have to match. Appended last, below.
  const action = el('span', 'sr-only', ' — apri la scheda');
  // Name and zone carry the marks, because those are the two fields a visitor
  // reads to decide whether a result is the one they meant. The rest of the
  // card is metadata and does not need to argue for itself.
  const name = el('h3', 'card__name');
  name.append(highlight(venue.nome ?? venue.id, state.q));
  open.append(name);

  const km =
    distance && state.me && venue.lat !== null && venue.lng !== null
      ? distanceKm(state.me, { lat: venue.lat, lng: venue.lng })
      : null;
  // A missing zone is left out rather than announced: "Zona non indicata" told
  // the visitor nothing they could use.
  const parts = [];
  // On an episode's page the street, where it is confirmed: the four venues
  // share a town, and the town alone would tell them apart from nothing.
  const where =
    address && venue.indirizzo && venue.indirizzo_confermato ? venue.indirizzo : venue.zona;
  if (where) parts.push(highlight(where, state.q));
  // The distance is ours, not the catalogue's, so nothing in it can match.
  if (km !== null) parts.push(document.createTextNode(formatDistance(km)));
  if (parts.length) {
    const zoneLine = el('p', 'card__zone');
    parts.forEach((part, i) => {
      if (i) zoneLine.append(document.createTextNode(' · '));
      zoneLine.append(part);
    });
    open.append(zoneLine);
  }

  const why = matchNote(venue, state.q);
  if (why) open.append(why);
  open.append(action);
  open.addEventListener('click', () => onOpen(venue.id));
  row.append(open);
  row.append(favouriteButton(venue, ctx));
  card.append(row);

  const meta = el('div', 'card__meta');
  meta.append(
    programPill(venue.programma, programNames.get(venue.programma) ?? venue.programma)
  );
  const outcome = outcomeLabel(venue);
  if (outcome) meta.append(el('span', 'tag tag--win', outcome));
  // A starred restaurant's number is its stars, where a programme's is a win.
  if (isGuide(venue) && venue.stelle) {
    const stars = el('span', 'tag tag--stars', starsGlyph(venue.stelle));
    stars.setAttribute('aria-label', venue.stelle === 1 ? '1 stella Michelin' : `${venue.stelle} stelle Michelin`);
    meta.append(stars);
  }
  meta.append(
    el('span', `state state--${venue.stato}`, STATUS_LABELS[venue.stato])
  );
  card.append(meta);

  // Only when there is an episode to name: "Puntata non identificata" was a
  // line about our data, not about the venue. Linked to the episode's page
  // where it has one, except on that page itself.
  if (venue.puntata?.titolo || venue.puntata?.data) {
    const line = el('p', 'card__episode');
    const page = episodeLink ? ctx.episodes?.byKey.get(venueEpisodeKey(venue)) : null;
    if (page && ctx.onOpenEpisode) {
      const link = el('a', 'card__episode-link', formatEpisode(venue.puntata));
      link.href = `/puntata/${page.slug}`;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        ctx.onOpenEpisode(page.slug);
      });
      line.append(link);
    } else {
      line.textContent = formatEpisode(venue.puntata);
    }
    card.append(line);
  }


  return card;
}

export function emptyState(title, text) {
  const box = el('div', 'empty');
  box.append(el('strong', null, title));
  box.append(document.createTextNode(text));
  return box;
}

/**
 * How many cards a list draws before it asks.
 *
 * The catalogue went from forty venues to a thousand, and every list was
 * still drawn whole: the "Elenco" tab built a thousand cards on every redraw,
 * including the one a single heart triggers, which a cheap phone feels. A
 * page at a time keeps the work the size of the screen.
 */
const PAGE = 60;

/** Cards already opened in each list, so a redraw does not fold them back. */
const shownIn = new WeakMap();

/**
 * Folds every list back to its first page.
 *
 * Called when what the lists contain changes — a filter, a query — and not
 * when it is only redrawn: a heart pressed eighty cards down must not throw
 * the visitor back to the top.
 */
export function resetLists() {
  for (const container of document.querySelectorAll('[data-paged]')) {
    shownIn.delete(container);
  }
}

export function renderList(container, venues, ctx, options = {}) {
  const {
    emptyTitle = 'Nessun locale',
    emptyText = 'Prova a togliere un filtro.',
    ...cardOptions
  } = options;

  if (venues.length === 0) {
    container.replaceChildren(emptyState(emptyTitle, emptyText));
    return;
  }

  // At least a page, as much as was already open, and always far enough to
  // include the venue selected on the map: the wide layout scrolls to its
  // card, and focus comes back to it when a card is closed.
  let shown = Math.max(PAGE, shownIn.get(container) ?? 0);
  const active = venues.findIndex((venue) => venue.id === ctx.state.attivo);
  if (active >= shown) shown = Math.ceil((active + 1) / PAGE) * PAGE;
  shownIn.set(container, shown);
  container.dataset.paged = '';

  const fragment = document.createDocumentFragment();
  for (const venue of venues.slice(0, shown)) {
    fragment.append(createCard(venue, ctx, cardOptions));
  }
  container.replaceChildren(fragment);
  appendMore(container, venues, ctx, cardOptions, shown);
}

/** The button that draws the next page, below the last card. */
function appendMore(container, venues, ctx, cardOptions, shown) {
  const left = venues.length - shown;
  if (left <= 0) return;

  const more = el(
    'button',
    'btn btn--wide btn--spaced',
    left > PAGE ? `Mostra altri ${PAGE} (ne restano ${left})` : `Mostra gli ultimi ${left}`
  );
  more.type = 'button';
  more.addEventListener('click', () => {
    const next = shown + PAGE;
    shownIn.set(container, next);
    const fragment = document.createDocumentFragment();
    for (const venue of venues.slice(shown, next)) {
      fragment.append(createCard(venue, ctx, cardOptions));
    }
    const first = fragment.firstElementChild;
    more.replaceWith(fragment);
    appendMore(container, venues, ctx, cardOptions, next);
    // The button that had the focus is gone; the first new card takes it.
    first?.querySelector('.card__open')?.focus({ preventScroll: true });
  });
  container.append(more);
}
